import { useEffect, useMemo, useRef, useState } from "react"
import { BookOpen, FileText, Folder, Loader2, Upload, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@f2e/ui/components/base"
import { buildFileAcceptString } from "@f2e/shared/constants"
import { useStore } from "@/store"
import { useDragAndDrop, useToast } from "@/hooks"
import {
  useDeleteAgentTemplateKnowledgeUploadApi,
  useBatchKnowledgeBaseNodesApi,
  useUploadAgentTemplateKnowledgeApi,
} from "@/hooks/api"
import type { KnowledgeNodeSchema, StagedUploadedBlobSchema } from "@/hooks/api"
import {
  MAX_FILE_SIZE_MB,
  MAX_KB_UPLOAD_FILES,
  SUPPORTED_KB_MIME_TYPES,
  validateFiles,
} from "@/lib/fileUtils"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

import { KnowledgeBaseToolSection } from "../../KnowledgeBaseToolSection"
import type { UnifiedTool } from "../../type"
import { resolveLocalizedText } from "../registry"
import type {
  AgentTemplateKnowledgeBaseFieldDefinition,
  AgentTemplateToolValue,
} from "../types"
import { ConfigurableFieldShell } from "./ConfigurableFieldShell"
import type { TemplateItemRendererProps } from "./TemplateItemRenderer"

export const KnowledgeBaseItemRenderer = ({
  workspaceId,
  item,
  locale,
  value,
  metadata,
  error,
  disabled,
  onPendingChange,
  onMetadataChange,
  onChange,
}: TemplateItemRendererProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const organizationId = useStore(
    (state: { currentOrganization: { id?: string } | null }) =>
      state.currentOrganization?.id
  )
  const [pickerOpen, setPickerOpen] = useState(false)
  const [draftNodeIds, setDraftNodeIds] = useState<string[]>([])
  const [selectedNodes, setSelectedNodes] = useState<KnowledgeNodeSchema[]>([])
  const stagedFiles = Array.isArray(metadata)
    ? (metadata as StagedUploadedBlobSchema[])
    : []
  const stagedFilesRef = useRef(stagedFiles)
  stagedFilesRef.current = stagedFiles
  const [uploading, setUploading] = useState(false)
  const uploadingRef = useRef(false)
  const mountedRef = useRef(true)
  const onPendingChangeRef = useRef(onPendingChange)
  onPendingChangeRef.current = onPendingChange
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { fetchFn: uploadFiles } = useUploadAgentTemplateKnowledgeApi()
  const { fetchFn: deleteUpload } = useDeleteAgentTemplateKnowledgeUploadApi()
  const { fetchFn: resolveSelectedNodes } = useBatchKnowledgeBaseNodesApi()
  const resolveSelectedNodesRef = useRef(resolveSelectedNodes)
  useEffect(() => {
    resolveSelectedNodesRef.current = resolveSelectedNodes
  }, [resolveSelectedNodes])
  const definition = item as AgentTemplateKnowledgeBaseFieldDefinition
  const current = (value as AgentTemplateToolValue | undefined) ?? {
    enabled: !!(definition.required || definition.enabledByDefault),
    configuration: {},
  }
  const currentRef = useRef(current)
  currentRef.current = current
  const configuration = current.configuration as Record<string, unknown>
  const selectedNodeIds = Array.isArray(configuration.selectedNodeIds)
    ? (configuration.selectedNodeIds as string[])
    : []
  const stagedUploadIds = useMemo(
    () =>
      Array.isArray(configuration.stagedUploadIds)
        ? (configuration.stagedUploadIds as number[])
        : [],
    [configuration.stagedUploadIds]
  )
  const stagedUploadIdsRef = useRef(stagedUploadIds)
  useEffect(() => {
    stagedUploadIdsRef.current = stagedUploadIds
  }, [stagedUploadIds])
  const selectedNodeIdsKey = selectedNodeIds.join(",")

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (uploadingRef.current) {
        uploadingRef.current = false
        onPendingChangeRef.current?.(false)
      }
    }
  }, [])

  useEffect(() => {
    const ids = selectedNodeIdsKey ? selectedNodeIdsKey.split(",") : []
    if (!organizationId || !workspaceId || ids.length === 0) {
      setSelectedNodes([])
      return
    }
    let cancelled = false
    setSelectedNodes([])
    resolveSelectedNodesRef
      .current({
        urlBuilderParams: { organizationId, workspaceId },
        jsonBody: { ids, include_deleted: true },
      })
      .then((response) => {
        if (cancelled) return
        const nodesById = new Map(
          (response?.data ?? []).map((node) => [String(node.id), node])
        )
        setSelectedNodes(
          ids
            .map((id) => nodesById.get(id))
            .filter((node): node is KnowledgeNodeSchema => !!node)
        )
      })
      .catch(() => {
        if (!cancelled) setSelectedNodes([])
      })
    return () => {
      cancelled = true
    }
  }, [organizationId, selectedNodeIdsKey, workspaceId])
  const pickerTool: UnifiedTool = {
    id: definition.key,
    type: "knowledge_base",
    knowledgeNodeIds: draftNodeIds,
  }
  const openPicker = () => {
    setDraftNodeIds(selectedNodeIds)
    setPickerOpen(true)
  }
  const commitStagedFiles = (
    files: StagedUploadedBlobSchema[],
    uploadIds: number[]
  ) => {
    const latestValue = currentRef.current
    const latestConfiguration = latestValue.configuration as Record<
      string,
      unknown
    >
    stagedFilesRef.current = files
    stagedUploadIdsRef.current = uploadIds
    onMetadataChange?.(files)
    onChange({
      enabled: latestValue.enabled || uploadIds.length > 0,
      configuration: {
        ...latestConfiguration,
        stagedUploadIds: uploadIds,
      },
    })
  }
  const handleFiles = async (files: File[]) => {
    if (!files.length || disabled || uploadingRef.current) return
    const { validFiles, unsupportedFiles, oversizedFiles } = validateFiles(
      files,
      [],
      MAX_FILE_SIZE_MB,
      SUPPORTED_KB_MIME_TYPES
    )
    if (unsupportedFiles.length > 0) {
      alertToast({
        title: t("workspace.agent.templates.knowledgeBase.uploadFailed"),
        description: t("common.fileUpload.error.unsupportedType", {
          files: unsupportedFiles.map((file) => file.name).join(", "),
        }),
      })
    }
    if (oversizedFiles.length > 0) {
      alertToast({
        title: t("workspace.agent.templates.knowledgeBase.uploadFailed"),
        description: t("common.fileUpload.error.fileTooLarge", {
          limit: MAX_FILE_SIZE_MB,
          files: oversizedFiles.map((file) => file.name).join(", "),
        }),
      })
    }
    if (validFiles.length === 0) return
    if (stagedUploadIds.length + validFiles.length > MAX_KB_UPLOAD_FILES) {
      alertToast({
        title: t("workspace.agent.templates.knowledgeBase.uploadFailed"),
        description: t("common.fileUpload.error.maxFiles", {
          limit: MAX_KB_UPLOAD_FILES,
        }),
      })
      return
    }
    uploadingRef.current = true
    const form = new FormData()
    validFiles.forEach((file) => form.append("files", file))
    setUploading(true)
    onPendingChangeRef.current?.(true)
    try {
      const response = await uploadFiles({
        body: form,
        searchParams: new URLSearchParams({ workspace_id: workspaceId }),
      })
      const uploaded = response?.data?.files || []
      const uploadErrors = response?.data?.errors || []
      if (!mountedRef.current) return
      commitStagedFiles(
        [...stagedFilesRef.current, ...uploaded],
        [...stagedUploadIdsRef.current, ...uploaded.map((file) => file.id)]
      )
      if (uploadErrors.length > 0) {
        alertToast({
          title: t("workspace.agent.templates.knowledgeBase.uploadFailed"),
          description: uploadErrors
            .slice(0, 3)
            .map((item) => {
              if (item.code === "empty_file") {
                return t("common.fileUpload.error.emptyFile", {
                  files: item.name,
                })
              }
              if (item.code === "file_too_large") {
                return t("common.fileUpload.error.fileTooLarge", {
                  limit: MAX_FILE_SIZE_MB,
                  files: item.name,
                })
              }
              if (item.code === "unsupported_file_type") {
                return t("common.fileUpload.error.unsupportedType", {
                  files: item.name,
                })
              }
              return t("common.fileUpload.error.invalidFile", {
                files: item.name,
              })
            })
            .join("\n"),
        })
      }
    } catch (error) {
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("workspace.agent.templates.knowledgeBase.uploadFailed"),
          fallbackDescription: t(
            "workspace.agent.templates.knowledgeBase.uploadFailedDescription"
          ),
        }),
      })
    } finally {
      uploadingRef.current = false
      if (mountedRef.current) {
        onPendingChangeRef.current?.(false)
        setUploading(false)
      }
    }
  }
  const { isDragging, dragHandlers } = useDragAndDrop({
    onFilesDropped: handleFiles,
    logPrefix: "AgentTemplateKnowledgeBase",
  })
  const removeStagedFile = async (fileId: number) => {
    commitStagedFiles(
      stagedFilesRef.current.filter((file) => file.id !== fileId),
      stagedUploadIdsRef.current.filter((id) => id !== fileId)
    )
    try {
      await deleteUpload({ urlBuilderParams: { uploadId: fileId } })
    } catch {
      // Expiry cleanup is the correctness fallback; removal from this draft is immediate.
    }
  }
  const removeSelectedNode = (nodeId: string) => {
    onChange({
      enabled: current.enabled,
      configuration: {
        ...configuration,
        selectedNodeIds: selectedNodeIds.filter((id) => id !== nodeId),
      },
    })
  }
  const interactionDisabled = disabled || uploading

  return (
    <>
      <ConfigurableFieldShell
        id={`template-${definition.key}`}
        title={resolveLocalizedText(definition.label, locale)}
        description={
          definition.description
            ? resolveLocalizedText(definition.description, locale)
            : undefined
        }
        enabled={current.enabled}
        required={definition.required}
        disabled={interactionDisabled}
        summary={
          selectedNodeIds.length > 0 || stagedUploadIds.length > 0 ? (
            <span className="inline-flex items-center gap-1.5">
              <BookOpen className="size-3.5" aria-hidden="true" />
              {t("workspace.agent.templates.knowledgeBase.selectionSummary", {
                count: selectedNodeIds.length + stagedUploadIds.length,
              })}
            </span>
          ) : undefined
        }
        error={error}
        onEnabledChange={(enabled) =>
          onChange({ enabled, configuration: current.configuration })
        }
      >
        <div className="space-y-3 border-t border-border pt-4">
          {selectedNodeIds.length === 0 && stagedUploadIds.length === 0 && (
            <div
              className={`flex min-h-28 flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
                isDragging ? "border-primary bg-primary/5" : "border-border"
              }`}
              {...(interactionDisabled ? {} : dragHandlers)}
            >
              <Upload className="mb-2 size-6 text-muted-foreground" />
              <p className="text-sm font-medium">
                {t("workspace.agent.templates.knowledgeBase.dropFiles")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("workspace.agent.templates.knowledgeBase.pendingHint")}
              </p>
            </div>
          )}
          {(selectedNodes.length > 0 || stagedFiles.length > 0) && (
            <div className="space-y-2">
              {selectedNodes.map((node) => (
                <div
                  key={node.id}
                  className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  {node.node_type === "FOLDER" ? (
                    <Folder className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <FileText className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{node.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={t("common.remove")}
                    disabled={interactionDisabled}
                    onClick={() => removeSelectedNode(String(node.id))}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
              {stagedFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={t("common.remove")}
                    disabled={interactionDisabled}
                    onClick={() => removeStagedFile(file.id)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {(selectedNodeIds.length > 0 || stagedUploadIds.length > 0) && (
            <div
              className={`flex min-h-12 items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2 text-center text-xs font-medium transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5 text-foreground"
                  : "border-border text-foreground"
              }`}
              {...(interactionDisabled ? {} : dragHandlers)}
            >
              <Upload
                className="size-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              {t("workspace.agent.templates.knowledgeBase.dropMoreFiles")}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            {t("workspace.agent.templates.knowledgeBase.uploadRequirements", {
              limit: MAX_FILE_SIZE_MB,
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={interactionDisabled}
              onClick={openPicker}
            >
              {selectedNodeIds.length
                ? t("workspace.agent.templates.knowledgeBase.editSelection")
                : t("workspace.agent.templates.knowledgeBase.chooseExisting")}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={interactionDisabled}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Upload className="mr-2 size-4" />
              )}
              {t("workspace.agent.templates.knowledgeBase.uploadFiles")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={buildFileAcceptString(SUPPORTED_KB_MIME_TYPES)}
              disabled={interactionDisabled}
              className="hidden"
              onChange={(event) => {
                void handleFiles(Array.from(event.target.files || []))
                event.target.value = ""
              }}
            />
          </div>
        </div>
      </ConfigurableFieldShell>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="flex h-dvh max-h-dvh flex-col rounded-none sm:h-auto sm:max-h-[85vh] sm:max-w-[760px] sm:rounded-lg">
          <DialogHeader>
            <DialogTitle>
              {t("workspace.agent.templates.knowledgeBase.pickerTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("workspace.agent.templates.knowledgeBase.pickerDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-1">
            <KnowledgeBaseToolSection
              tool={pickerTool}
              organizationId={organizationId}
              workspaceId={workspaceId}
              disabled={interactionDisabled}
              showInvocationInstruction={false}
              onUpdateTool={(_toolId, updates) =>
                setDraftNodeIds(updates.knowledgeNodeIds ?? [])
              }
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPickerOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              disabled={interactionDisabled}
              onClick={() => {
                onChange({
                  enabled: current.enabled || draftNodeIds.length > 0,
                  configuration: {
                    ...configuration,
                    selectedNodeIds: draftNodeIds,
                  },
                })
                setPickerOpen(false)
              }}
            >
              {t("workspace.agent.templates.knowledgeBase.useSelection", {
                count: draftNodeIds.length,
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
